/**
 * Modal Titlebar Drag
 *
 * Enables pointer-driven dragging for modal dialogs via their titlebars.
 */

import { Roles } from "../constants/roles";
import { FormBase } from "./form-base-form";
import {
    bindModalDragDocumentEnd,
    bindModalDragDocumentMove,
    bindModalDragPointerDown,
    unbindModalDragDocumentEnd,
    unbindModalDragDocumentMove,
} from "./modal-titlebar-drag-listener";

interface DragOptions {
    handleSelector?: string;
    cancelSelector?: string;
    isEnabled?: (() => boolean) | null;
}

interface DragState {
    pointerId: number;
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
}

interface CoordinateSpace {
    left: number;
    top: number;
    maxWidth: number;
    maxHeight: number;
}

class ModalTitlebarDragFormBase extends FormBase {
    public getHandle(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }

    public getClosest(root: Event | EventTarget | Element | null | undefined, selector: string): HTMLElement | null {
        return this.closest(root, selector, HTMLElement);
    }
}

const modalTitlebarDragFormBase = new ModalTitlebarDragFormBase();

function createDragState(): DragState | null {
    return null;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function parsePixelValue(value: string | null | undefined): number | null {
    if (typeof value !== "string") return null;

    const trimmed = value.trim();
    if (!trimmed || trimmed === "auto") return null;

    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}

function getCoordinateSpace(modalEl: HTMLElement): CoordinateSpace {
    const win = modalEl.ownerDocument?.defaultView;
    const viewportWidth = win?.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = win?.innerHeight || document.documentElement.clientHeight || 0;
    const parentEl = modalEl.parentElement;

    if (parentEl instanceof HTMLElement) {
        const rect = parentEl.getBoundingClientRect();
        return {
            left: rect.left,
            top: rect.top,
            maxWidth: Math.max(0, viewportWidth - rect.left),
            maxHeight: Math.max(0, viewportHeight - rect.top),
        };
    }

    return {
        left: 0,
        top: 0,
        maxWidth: viewportWidth,
        maxHeight: viewportHeight,
    };
}

export function enableModalTitlebarDrag(modalEl: HTMLElement, options: DragOptions = {}): void {
    if (!(modalEl instanceof HTMLElement)) return;
    if (modalEl.dataset.titlebarDragBound === "1") return;

    const handleSelector = String(options.handleSelector || `[data-role="${Roles.ELEMENT_MODAL_HEADER}"]`);
    const handleEl = modalTitlebarDragFormBase.getHandle(modalEl, handleSelector);
    if (!(handleEl instanceof HTMLElement)) return;

    const cancelSelector = String(
        options.cancelSelector ||
            `button, a, input, textarea, select, label, [role="button"], [data-role="${Roles.ELEMENT_MODAL_CLOSE}"], [data-no-drag]`,
    );

    const isEnabled = typeof options.isEnabled === "function" ? options.isEnabled : null;

    modalEl.dataset.titlebarDragBound = "1";
    modalEl.classList.add("is-draggable");

    let state = createDragState();

    const onPointerMove = (event: PointerEvent): void => {
        if (!state) return;
        if (event.pointerId !== state.pointerId) return;

        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;

        const rect = modalEl.getBoundingClientRect();
        const coordinateSpace = getCoordinateSpace(modalEl);

        const padding = 8;
        const minLeft = padding - coordinateSpace.left;
        const minTop = padding - coordinateSpace.top;
        const maxLeft = Math.max(minLeft, coordinateSpace.maxWidth - rect.width - padding);
        const maxTop = Math.max(minTop, coordinateSpace.maxHeight - rect.height - padding);

        const left = clamp(state.startLeft + dx, minLeft, maxLeft);
        const top = clamp(state.startTop + dy, minTop, maxTop);

        modalEl.style.left = `${left}px`;
        modalEl.style.top = `${top}px`;
    };

    const endDrag = (event: PointerEvent): void => {
        if (!state) return;
        if (event.pointerId !== state.pointerId) return;

        state = null;
        modalEl.classList.remove("is-dragging");
        try {
            handleEl.releasePointerCapture(event.pointerId);
        } catch {
            // ignore
        }
        unbindModalDragDocumentMove(onPointerMove);
        unbindModalDragDocumentEnd(endDrag);
    };

    bindModalDragPointerDown(handleEl, (event) => {
        if (!(event instanceof PointerEvent)) return;
        if (event.button !== 0) return;
        if (isEnabled && !isEnabled()) return;

        const target = event.target instanceof HTMLElement ? event.target : null;
        if (!target) return;
        if (modalTitlebarDragFormBase.getClosest(target, cancelSelector)) return;

        const rect = modalEl.getBoundingClientRect();
        if (!modalEl.dataset.dragInitialized) {
            const coordinateSpace = getCoordinateSpace(modalEl);
            let resolvedLeft = rect.left - coordinateSpace.left;
            let resolvedTop = rect.top - coordinateSpace.top;

            try {
                const view = modalEl.ownerDocument?.defaultView;
                const cs = view?.getComputedStyle ? view.getComputedStyle(modalEl) : null;
                const computedLeft = parsePixelValue(cs?.left);
                const computedTop = parsePixelValue(cs?.top);
                const nestedOffsetX = parsePixelValue(cs?.getPropertyValue("--nested-offset-x")) || 0;
                const nestedOffsetY = parsePixelValue(cs?.getPropertyValue("--nested-offset-y")) || 0;

                if (computedLeft !== null) {
                    resolvedLeft = computedLeft + nestedOffsetX;
                }

                if (computedTop !== null) {
                    resolvedTop = computedTop + nestedOffsetY;
                }

                if (!cs || cs.position !== "fixed") {
                    modalEl.style.position = "fixed";
                }
            } catch {
                modalEl.style.position = "fixed";
            }

            modalEl.style.left = `${resolvedLeft}px`;
            modalEl.style.top = `${resolvedTop}px`;
            modalEl.style.width = `${rect.width}px`;
            modalEl.style.height = `${rect.height}px`;
            modalEl.style.right = "auto";
            modalEl.style.bottom = "auto";
            modalEl.style.transform = "none";
            modalEl.style.margin = "0";
            modalEl.style.removeProperty("--nested-offset-x");
            modalEl.style.removeProperty("--nested-offset-y");
            modalEl.dataset.dragInitialized = "1";
        }

        const startRect = modalEl.getBoundingClientRect();
        const coordinateSpace = getCoordinateSpace(modalEl);
        state = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startLeft: startRect.left - coordinateSpace.left,
            startTop: startRect.top - coordinateSpace.top,
        };

        modalEl.classList.add("is-dragging");
        modalEl.style.willChange = "left, top";

        try {
            handleEl.setPointerCapture(event.pointerId);
        } catch {
            // ignore
        }

        bindModalDragDocumentMove(onPointerMove);
        bindModalDragDocumentEnd(endDrag);
    });
}
